import {
    type CSSProperties,
    useLayoutEffect,
    useEffect,
    useState,
    useMemo,
    useRef
} from "react";
import {
    useWindowDimensions
} from "react-native";
import {
    isBlocksPath,
    BLOCKS_PATH
} from "./blocks";
import {
    Pager
} from "./components/content";
import SearchDialog from "./components/searchDialog";
import Sidebar from "./components/sidebar";
import Toc, {
    type DocsTocItem
} from "./components/toc";
import TopBar from "./components/topBar";
import {
    useDocsI18n
} from "./i18n";
import {
    CREATE_PATH,
    findPage
} from "./nav";
import BlocksIndexPage, {
    BLOCK_PAGES
} from "./pages/blocks";
import {
    COMPONENT_PAGES
} from "./pages/components";
import CreatePage from "./pages/create";
import {
    FOUNDATION_PAGES
} from "./pages/foundations";
import GuidePage, {
    getGuideToc,
    GUIDES
} from "./pages/guide";
import InstallationPage, {
    getInstallationToc
} from "./pages/installation";
import IntroductionPage, {
    getIntroductionToc
} from "./pages/introduction";
import MotionPage, {
    getMotionToc
} from "./pages/motion";
import {
    RELEASE_NOTE_PAGES
} from "./pages/releaseNotes";
import {
    useDocsRouter
} from "./router";
import {
    type DocsThemePreference,
    DocsWidthContext,
    useDocsLayout,
    resolveTheme
} from "./theme";
import {
    getNCoreUIKitVersion,
    getMotionTransition,
    getFocusRingStyle,
    useReducedMotion,
    NCoreUIKitTheme,
    getCornerStyle,
    Text
} from "ncore-ui-kit";

const THEME_STORAGE_KEY = "ncore-docs-theme";

const WIDTH_STORAGE_KEY = "ncore-docs-width";

const DOCS_VERSION = getNCoreUIKitVersion();

const readStoredFullWidth = () => {
    try {
        return window.localStorage.getItem(WIDTH_STORAGE_KEY) === "full";
    } catch {
        return false;
    }
};

export const readStoredThemePreference = (): DocsThemePreference => {
    try {
        const storedPreference = window.localStorage.getItem(THEME_STORAGE_KEY);

        if(storedPreference === "light" || storedPreference === "dark") {
            return storedPreference;
        }
    } catch {
        return "system";
    }

    return "system";
};

const DocsApp = () => {
    const {
        path
    } = useDocsRouter();

    const {
        language,
        t
    } = useDocsI18n();

    const {
        activeThemePreference,
        controlPaddings,
        controlHeights,
        shapeConfigs,
        typography,
        durations,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        headerHeight,
        maxWidth
    } = useDocsLayout();

    const {
        width
    } = useWindowDimensions();

    const isReduceMotionEnabled = useReducedMotion();

    const [
        isSkipFocused,
        setIsSkipFocused
    ] = useState(false);

    const [
        themePreference,
        setThemePreference
    ] = useState<DocsThemePreference>(readStoredThemePreference);

    const syncedKitPreferenceRef = useRef<string>(activeThemePreference);

    const [
        isMenuOpen,
        setIsMenuOpen
    ] = useState(false);

    const [
        isFullWidth,
        setIsFullWidth
    ] = useState(readStoredFullWidth);

    useEffect(() => {
        document.documentElement.setAttribute("data-width", isFullWidth ? "full" : "default");

        try {
            if(isFullWidth) {
                window.localStorage.setItem(WIDTH_STORAGE_KEY, "full");
            } else {
                window.localStorage.removeItem(WIDTH_STORAGE_KEY);
            }
        } catch {
            document.documentElement.setAttribute("data-width", isFullWidth ? "full" : "default");
        }
    }, [
        isFullWidth
    ]);

    const [
        isSearchOpen,
        setIsSearchOpen
    ] = useState(false);

    useEffect(() => {
        if(activeThemePreference === syncedKitPreferenceRef.current) {
            return;
        }

        syncedKitPreferenceRef.current = activeThemePreference;

        if(activeThemePreference === "light" || activeThemePreference === "dark" || activeThemePreference === "system") {
            setThemePreference(activeThemePreference);
        }
    }, [
        activeThemePreference
    ]);

    useLayoutEffect(() => {
        const bodyStyle = document.body.style;

        bodyStyle.backgroundColor = colors.content.container.default;
        bodyStyle.colorScheme = resolveTheme(themePreference);
        bodyStyle.color = colors.content.text.high;
        bodyStyle.fontFamily = typography.bodyLargeSize.fontFamily ?? "";
        bodyStyle.fontSize = `${typography.bodyLargeSize.fontSize}px`;
        bodyStyle.lineHeight = `${typography.bodyLargeSize.lineHeight}px`;
        bodyStyle.touchAction = "manipulation";
        bodyStyle.margin = "0";

        document.documentElement.style.touchAction = "manipulation";
    }, [
        themePreference,
        typography,
        colors
    ]);

    useEffect(() => {
        const applyTheme = () => {
            const themeName = resolveTheme(themePreference);

            document.documentElement.setAttribute("data-theme", themeName);
        };

        applyTheme();

        syncedKitPreferenceRef.current = themePreference;

        if(NCoreUIKitTheme.state.activeThemePreference !== themePreference) {
            NCoreUIKitTheme.switch({
                themeKey: themePreference
            });
        }

        try {
            if(themePreference === "system") {
                window.localStorage.removeItem(THEME_STORAGE_KEY);
            } else {
                window.localStorage.setItem(THEME_STORAGE_KEY, themePreference);
            }
        } catch {
            applyTheme();
        }

        const media = window.matchMedia("(prefers-color-scheme: dark)");

        media.addEventListener("change", applyTheme);

        return () => {
            media.removeEventListener("change", applyTheme);
        };
    }, [
        themePreference
    ]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if(event.key === "Escape") {
                setIsMenuOpen(false);
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    useEffect(() => {
        setIsMenuOpen(false);
    }, [
        path
    ]);

    const page = findPage(path);

    const pageTitle = page ? page.title ?? t(page.titleKey ?? "") : "NCore UI Kit";

    useEffect(() => {
        document.title = path === "/" ? "NCore UI Kit · Docs" : `${pageTitle} · NCore UI Kit`;
    }, [
        pageTitle,
        language,
        path
    ]);

    const getToc = (): Array<DocsTocItem> => {
        if(path === "/") {
            return getIntroductionToc(t);
        }

        if(path === "/installation") {
            return getInstallationToc(t);
        }

        if(path === "/foundations/motion") {
            return getMotionToc(t);
        }

        if(FOUNDATION_PAGES[path]) {
            return FOUNDATION_PAGES[path].getToc(t);
        }

        if(RELEASE_NOTE_PAGES[path]) {
            return RELEASE_NOTE_PAGES[path].getToc(t);
        }

        if(BLOCK_PAGES[path]) {
            return BLOCK_PAGES[path].getToc();
        }

        if(GUIDES[path]) {
            return getGuideToc(path, t);
        }

        const componentEntry = page?.title ? COMPONENT_PAGES[page.title] : undefined;

        if(componentEntry && page?.path.startsWith("/components/")) {
            return componentEntry.getToc(t);
        }

        return [];
    };

    const renderPage = () => {
        if(path === "/") {
            return <IntroductionPage/>;
        }

        if(path === "/installation") {
            return <InstallationPage/>;
        }

        if(path === "/foundations/motion") {
            return <MotionPage/>;
        }

        const FoundationPage = FOUNDATION_PAGES[path]?.Page;

        if(FoundationPage) {
            return <FoundationPage/>;
        }

        const ReleaseNotePage = RELEASE_NOTE_PAGES[path]?.Page;

        if(ReleaseNotePage) {
            return <ReleaseNotePage/>;
        }

        if(path === BLOCKS_PATH) {
            return <BlocksIndexPage/>;
        }

        if(path === CREATE_PATH) {
            return <CreatePage/>;
        }

        const BlockPage = BLOCK_PAGES[path]?.Page;

        if(BlockPage) {
            return <BlockPage/>;
        }

        if(GUIDES[path]) {
            return <GuidePage
                path={path}
            />;
        }

        const ComponentPage = page?.title && page.path.startsWith("/components/") ? COMPONENT_PAGES[page.title]?.Page : undefined;

        if(ComponentPage) {
            return <ComponentPage/>;
        }

        return <IntroductionPage/>;
    };

    const toc = useMemo(getToc, [
        language,
        path
    ]);

    const isWide = width >= shapeConfigs.wideBreakpoint;

    const isWorkspace = isBlocksPath(path) || path === CREATE_PATH;

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const getMainPadding = (): CSSProperties => {
        if(isWide) {
            return {
                padding: spaces.spacingXxl
            };
        }

        const paddingHorizontal = isCompact ? spaces.spacingMd : spaces.spacingLg;

        return {
            padding: `${spaces.spacingXl}px ${paddingHorizontal}px ${spaces.spacingXxl}px`
        };
    };

    const skipStyle: CSSProperties = {
        ...isSkipFocused ? getFocusRingStyle({
            isFocusVisible: true,
            borders,
            colors
        }) as CSSProperties : {},
        top: isSkipFocused ? spaces.spacingSm : -controlHeights.large,
        padding: `${spaces.spacingSm}px ${controlPaddings.small}px`,
        backgroundColor: colors.content.container.inverse,
        zIndex: shapeConfigs.layers.overlay,
        ...getCornerStyle({
            radius: radiuses.actions,
            shapeConfigs
        }) as CSSProperties,
        left: spaces.spacingMd,
        textDecoration: "none",
        position: "absolute"
    };

    const rootStyle: CSSProperties = {
        flexDirection: "column",
        minHeight: "100vh",
        display: "flex"
    };

    const layoutStyle: CSSProperties = {
        ...getMotionTransition({
            isReduceMotionEnabled,
            intent: "expand",
            properties: [
                "max-width"
            ],
            durations
        }) as CSSProperties,
        maxWidth: isFullWidth ? "100%" : maxWidth,
        flexDirection: "row",
        margin: "0 auto",
        display: "flex",
        width: "100%",
        flex: 1
    };

    const columnStyle: CSSProperties = {
        width: isWide ? shapeConfigs.drawerWidth : 0,
        flexShrink: 0
    };

    const mainStyle: CSSProperties = {
        ...getMainPadding(),
        outline: "none",
        minWidth: 0,
        flex: 1
    };

    const scrimStyle: CSSProperties = {
        ...getMotionTransition({
            isReduceMotionEnabled,
            intent: "panelEnter",
            motion: "fade",
            properties: [
                "opacity"
            ],
            durations
        }) as CSSProperties,
        pointerEvents: isMenuOpen ? "auto" : "none",
        backgroundColor: colors.system.scrim,
        zIndex: shapeConfigs.layers.sticky,
        opacity: isMenuOpen ? 1 : 0,
        position: "fixed",
        top: headerHeight,
        bottom: 0,
        right: 0,
        left: 0
    };

    const scrim = isWide ? null : <div
        onClick={() => setIsMenuOpen(false)}
        style={scrimStyle}
        aria-hidden="true"
    />;

    const inlineToc = isWide ? null : <Toc
        key={`inline-${path}-${language}`}
        variant="inline"
        items={toc}
    />;

    const railToc = isWide && toc.length > 0 ? <div
        style={columnStyle}
    >
        <Toc
            key={`rail-${path}-${language}`}
            variant="rail"
            items={toc}
        />
    </div> : null;

    return <div
        style={rootStyle}
    >
        <a
            onBlur={() => setIsSkipFocused(false)}
            onFocus={() => setIsSkipFocused(true)}
            style={skipStyle}
            href="#docs-main"
        >
            <Text
                customColor={colors.content.container.default}
                variant="bodyMediumSize"
            >
                {t("skip")}
            </Text>
        </a>
        <TopBar
            onWidthToggle={() => setIsFullWidth(!isFullWidth)}
            onMenuToggle={() => setIsMenuOpen(!isMenuOpen)}
            onThemePreferenceChange={setThemePreference}
            onSearchOpen={() => setIsSearchOpen(true)}
            themePreference={themePreference}
            isFullWidth={isFullWidth}
            isMenuOpen={isMenuOpen}
            version={DOCS_VERSION}
            maxWidth={maxWidth}
        />
        <div
            style={layoutStyle}
        >
            {
                isWorkspace && isWide ?
                    null
                :
                    <div
                        style={columnStyle}
                    >
                        <Sidebar
                            onNavigate={() => setIsMenuOpen(false)}
                            isOpen={isMenuOpen}
                        />
                    </div>
            }
            {scrim}
            <main
                style={mainStyle}
                id="docs-main"
                tabIndex={-1}
            >
                <DocsWidthContext.Provider
                    value={isFullWidth || isWorkspace}
                >
                    {inlineToc}
                    {renderPage()}
                    {
                        isWorkspace ?
                            null
                        :
                            <Pager
                                path={path}
                            />
                    }
                </DocsWidthContext.Provider>
            </main>
            {railToc}
        </div>
        <SearchDialog
            onOpenChange={setIsSearchOpen}
            isOpen={isSearchOpen}
        />
    </div>;
};
export default DocsApp;
